import { useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { NumberPad } from './NumberPad';
import { cn } from '@/lib/utils';

interface QtyStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
}

/** Large −/+ stepper; tapping the count opens a NumberPad for direct entry. */
export function QtyStepper({ value, onChange, min = 0, max = 999, className }: QtyStepperProps) {
  const [padOpen, setPadOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  return (
    <>
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        <Button
          variant="outline"
          size="icon"
          className="h-14 w-14"
          disabled={value <= min}
          onClick={() => onChange(clamp(value - 1))}
          aria-label="Kurangi"
        >
          <Minus className="h-5 w-5" />
        </Button>
        <button
          type="button"
          className="tnum h-14 min-w-[3.5rem] rounded-md border-2 border-border bg-card px-3 text-center font-display text-2xl font-bold tabular-nums transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            setDraft(value);
            setPadOpen(true);
          }}
        >
          {value}
        </button>
        <Button
          variant="outline"
          size="icon"
          className="h-14 w-14"
          disabled={value >= max}
          onClick={() => onChange(clamp(value + 1))}
          aria-label="Tambah"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </div>

      <Dialog open={padOpen} onOpenChange={setPadOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Jumlah</DialogTitle>
          </DialogHeader>
          <div className="tnum text-center font-display text-4xl font-bold">{draft}</div>
          <NumberPad value={draft} onChange={setDraft} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPadOpen(false)}>
              Batal
            </Button>
            <Button
              onClick={() => {
                onChange(clamp(draft));
                setPadOpen(false);
              }}
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
